import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Badge } from "flowbite-react";
import { useForm } from "react-hook-form";
import {
  Briefcase,
  Users,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Flag,
  Book,
  Heart,
} from "lucide-react";
import { useGetData } from "../hooks/useGetData";
import { EvaluadoInterface } from "../interfaces/EvaluadoInterface";
import { motion, AnimatePresence } from "framer-motion";

// Añadimos props opcionales para permitir controlar el componente desde fuera
interface EvaluadoInfoProps {
  externalIsOpen?: boolean;
  externalToggle?: () => void;
  hideButton?: boolean;
}

const EvaluadoInfo: React.FC<EvaluadoInfoProps> = ({
  externalIsOpen,
  externalToggle,
  hideButton = false,
}) => {
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { control, reset } = useForm();
  const navigate = useNavigate();

  // Utilizamos el estado interno solo si no se proporciona control externo
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Determinamos si usar el estado interno o externo
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const toggleBlind = externalToggle || (() => setInternalIsOpen(!internalIsOpen));

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const genero = queryEvaluado.data?.genero;

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="flex flex-col items-center w-full">
      {/* Botón para desplegar persiana - solo se muestra si hideButton es false */}
      {!hideButton && (
        <motion.div className="top-[80px] left-0 right-0 z-20 w-full max-w-[400px]">
          <motion.button
            onClick={toggleBlind}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-b-lg focus:outline-none overflow-hidden"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div
              className="relative w-full h-8"
              initial={false}
              animate={isOpen ? "open" : "closed"}
            >
              <motion.div
                variants={{
                  open: {
                    scaleX: 1.5,
                    opacity: 0,
                    transition: { duration: 0.5 },
                  },
                  closed: {
                    scaleX: 1,
                    opacity: 0.5,
                    transition: { duration: 0.5 },
                  },
                }}
              />
              {/* Texto animado del botón */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center z-10 pointer-events-auto"
                variants={{
                  open: { y: -20, opacity: 0 },
                  closed: { y: 0, opacity: 1 },
                }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-lg font-semibold">
                  Información del evaluado
                </span>
              </motion.div>
              <motion.div
                className="absolute inset-0 flex items-center justify-center z-10 pointer-events-auto"
                variants={{
                  open: { y: 0, opacity: 1 },
                  closed: { y: 20, opacity: 0 },
                }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-lg font-semibold">
                  Cerrar información del evaluado
                </span>
              </motion.div>
            </motion.div>
          </motion.button>
        </motion.div>
      )}

      {/* Contenido desplegable */}
      <div className="top-[200px] left-0 right-0 z-10 w-full flex justify-center">
        <motion.div
          className="mb-4 w-full max-w-[900px]"
          initial={false}
          animate={{ height: isOpen ? "auto" : 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className="p-6 space-y-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-xl transition-all duration-300 max-w-4xl mx-auto"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.div variants={sectionVariants}>
                    <h2 className="text-3xl font-bold text-gray-800 border-b-2 border-blue-500 pb-2 mb-6">
                      Evaluando a: {queryEvaluado.data?.nombre}{" "}
                      {queryEvaluado.data?.apellido_paterno}{" "}
                      {queryEvaluado.data?.apellido_materno}
                    </h2>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        <User className="mr-2" /> Información de Contacto
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <Mail className="mr-2 text-blue-500" />
                          <span className="font-medium">Correo:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.correo}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="mr-2 text-blue-500" />
                          <span className="font-medium">Teléfono:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.telefono}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        <Briefcase className="mr-2" /> Información Laboral
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <span className="font-medium">Empresa:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.empresa_id?.nombre}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Puesto:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.puesto}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="mr-2 text-blue-500" />
                          <span className="font-medium">Fecha de Ingreso:</span>
                          <span className="ml-2 text-gray-600">
                            {formatDate(queryEvaluado.data?.fecha_ingreso)}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Antigüedad:</span>
                          <Badge color="success" className="ml-2">
                            {queryEvaluado.data?.antiguedad}{" "}
                          </Badge>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        <User className="mr-2" /> Datos Personales
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <Calendar className="mr-2 text-blue-500" />
                          <span className="font-medium">
                            Fecha de Nacimiento:
                          </span>
                          <span className="ml-2 text-gray-600">
                            {formatDate(queryEvaluado.data?.fecha_nacimiento)}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Género:</span>
                          <span className="ml-2 text-gray-600">
                            {genero ? generoMap[genero] : "No especificado"}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="mr-2 text-blue-500" />
                          <span className="font-medium">
                            Lugar de Nacimiento:
                          </span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.lugar_nacimiento}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Flag className="mr-2 text-blue-500" />
                          <span className="font-medium">Nacionalidad:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.nacionalidad}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">CURP:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.curp}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">RFC:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.rfc}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Book className="mr-2 text-blue-500" />
                          <span className="font-medium">Escolaridad:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.escolaridad}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className="mb-6">
                      <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        <MapPin className="mr-2" /> Dirección
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <span className="font-medium">Calle:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.calle}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Número Exterior:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.numero_exterior}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Número Interior:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.numero_interior || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Referencia:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.referencia}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Colonia:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.colonia}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Código Postal:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.codigo_postal}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Municipio:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.municipio}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Estado:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.estado}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">País:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.pais}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card>
                      <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                        <Users className="mr-2" /> Información Adicional
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <span className="font-medium">
                            Años viviendo en el domicilio:
                          </span>
                          <Badge color="success" className="ml-2">
                            {queryEvaluado.data?.anios_domicilio_viviendo} años
                          </Badge>
                        </div>
                        <div className="flex items-center">
                          <Users className="mr-2 text-blue-500" />
                          <span className="font-medium">
                            Personas viviendo en el domicilio:
                          </span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.personas_vivienda}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Heart className="mr-2 text-blue-500" />
                          <span className="font-medium">Estado Civil:</span>
                          <span className="ml-2 text-gray-600">
                            {queryEvaluado.data?.estado_civil}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default EvaluadoInfo;